﻿import React, { useMemo, useContext, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Context, { context_type } from '@Util/Context';
import { str_isEmpty } from '@Util/Utils';
import * as Api from '@Util/Api';
import { apiBase } from '@Util/GlobalValues';
import {
    Popup_Add_Project_Props,
    Select_Option
} from '@Util/Types';

import Popup from '@Elements/Popup/Popup';
import Input_With_Label from '@Elements/FormElements/Input_With_Label';
import Select_With_Label from '@Elements/FormElements/Select_With_Label';
import Submit_Button from '@Elements/FormElements/Submit_Button';

export default function Add_Project_Popup(props: Popup_Add_Project_Props): JSX.Element {
    const { t } = useTranslation();
    const {
        customers,
        fetch_customers,
        set_status_update
    }: context_type = useContext(Context);

    const [name, set_name] = useState<string>("");
    const [error, set_error] = useState<string | null>(null);
    const [saving, set_saving] = useState<boolean>(false);

    const customer_options: Select_Option[] = useMemo(() => {
        return customers.map(customer => ({
            text: customer.name,
            value: customer.id
        }));
    }, [customers]);

    const handle_add_project = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (saving) return;

        if (str_isEmpty(name)) {
            set_error(t('Error.ProjectNameEmpty'));
            return;
        }

        set_saving(true);
        set_error(null);

        try {
            const api_call = await Api.callApi(`${apiBase}/ScheduleEntry/create-project`, {
                customerId: props.customer_id,
                projectName: name.trim()
            });

            if (api_call?.status === "Invalid" || api_call?.status === "Error") {
                set_status_update({
                    visible: true,
                    text: api_call?.details,
                    type: "Error"
                });
            } else {
                props.set_open(false);
                set_name("");
                props.set_customer_id(0);
                set_status_update({
                    visible: true,
                    text: t('Text.ApiSaved'),
                    type: "Success"
                });
                fetch_customers();
            }
        } catch (err) {
            set_status_update({
                visible: true,
                text: err.message || t('Error.UnknownError'),
                type: "Error"
            });
        } finally {
            set_saving(false);
        }
    }, [props.customer_id, props.set_customer_id, name, saving, fetch_customers, set_status_update, t, props]);

    return (
        <Popup
            open={props.open}
            set_open={props.set_open}
            title={t('Text.AddProject')}
            disable_close_action={false}
        >
            <form onSubmit={handle_add_project}>
                <Select_With_Label
                    id="add_project_name_customer"
                    label={t('Label.Customer')}
                    value={props.customer_id}
                    onChange={(e) => props.set_customer_id(Number(e.target.value))}
                    error={Boolean(error)}
                    options={customer_options}
                />
                <Input_With_Label
                    type="text"
                    id="add_project_name"
                    label={t('Label.ProjectName')}
                    value={name}
                    onChange={(e) => set_name(e.target.value)}
                    error={Boolean(error)}
                />
                <Submit_Button
                    text={t('Button.Save')}
                    disabled={saving}
                    loading={saving}
                />
                {error && <div className="error-message">{error}</div>}
            </form>
        </Popup>
    );
}